import { useMemo } from 'react';

import { calculateIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount';

import type { WatchlistItem } from '@/hooks/useWatchlist';
import { useWatchlistSorting } from './useWatchlistSorting';
import { WatchlistSorting } from './types';

type SortingFunction<T> = (a: T, b: T) => -1 | 0 | 1;

export const sortValue = <Value extends string | number>(
  a: Value | null,
  b: Value | null,
  direction: 'asc' | 'desc'
) => {
  if (a === b) {
    return 0;
  }
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }
  if (direction === 'desc') {
    return a < b ? 1 : -1;
  }
  return a < b ? -1 : 1;
};

const sortingFns: Record<WatchlistSorting, SortingFunction<WatchlistItem>> = {
  [WatchlistSorting.RECENTLY_ADDED]: (
    { createdAt: a }: WatchlistItem,
    { createdAt: b }: WatchlistItem
  ) => sortValue(a, b, 'desc'),
  [WatchlistSorting.MARKET_CAP_HIGH_TO_LOW]: (
    {
      company: {
        analysisValue: { marketCapUSD: a },
      },
    }: WatchlistItem,
    {
      company: {
        analysisValue: { marketCapUSD: b },
      },
    }: WatchlistItem
  ) => sortValue(a, b, 'desc'),
  [WatchlistSorting.COMPANY_NAME_A_TO_Z]: (
    { company: { name: a } }: WatchlistItem,
    { company: { name: b } }: WatchlistItem
  ) => sortValue(a.toUpperCase(), b.toUpperCase(), 'asc'),
  [WatchlistSorting.TICKER_A_TO_Z]: (
    { company: { tickerSymbol: a } }: WatchlistItem,
    { company: { tickerSymbol: b } }: WatchlistItem
  ) => sortValue(a.toUpperCase(), b.toUpperCase(), 'asc'),
  [WatchlistSorting.PERFORMANCE_7D]: (
    {
      company: {
        analysisValue: { return7d: a },
      },
    }: WatchlistItem,
    {
      company: {
        analysisValue: { return7d: b },
      },
    }: WatchlistItem
  ) => sortValue(a, b, 'desc'),
  [WatchlistSorting.DISCOUNT_HIGH_TO_LOW]: (
    a: WatchlistItem,
    b: WatchlistItem
  ) =>
    sortValue(
      calculateIntrinsicDiscount(a.company, a.fairValue),
      calculateIntrinsicDiscount(b.company, b.fairValue),
      'desc'
    ),
  [WatchlistSorting.DISCOUNT_LOW_TO_HIGH]: (
    a: WatchlistItem,
    b: WatchlistItem
  ) =>
    sortValue(
      calculateIntrinsicDiscount(a.company, a.fairValue),
      calculateIntrinsicDiscount(b.company, b.fairValue),
      'asc'
    ),
};

export const useSortedWatchlistItems = (items: WatchlistItem[] = []) => {
  const [sorting] = useWatchlistSorting();

  return useMemo(() => {
    const sortingFn = sortingFns[sorting];

    if (typeof sortingFn === 'undefined') {
      return items;
    }

    return [...items].sort(sortingFn);
  }, [items, sorting]);
};
